import { useState } from 'react';
import { NavLink, Outlet, useOutletContext, useParams } from 'react-router-dom';
import {
  Archive,
  ArrowLeft,
  Building2,
  CreditCard,
  Inbox,
  Layers,
  Loader2,
  Lock,
  Mail,
  MapPin,
  Pencil,
  Printer,
  ShieldCheck,
  ShoppingCart,
  Star,
  Tag,
  UserCog,
  Users,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import {
  useAdminCustomerDetail,
  useSetCustomerName,
  type AdminCustomerDetail as CustomerDetail,
} from '@/hooks/admin/useAdminCustomerDetail';
import { useAdminAccountChangeRequests } from '@/hooks/admin/useAccountChangeRequestsAdmin';
import { useMyMemberships, useSetCustomerFavorite } from '@/hooks/useMemberships';
import { adminFetch } from '@/hooks/admin/useAdminApi';

/* Context handed down to the tab outlets so each tab gets the loaded customer
 * without refetching. */
export interface CustomerDetailContext {
  customer: CustomerDetail;
  refetch: () => void;
}

export function useCustomerDetailContext() {
  return useOutletContext<CustomerDetailContext>();
}

// Grouped like the top-level admin nav so the tab row reads as sections
// (Account / Billing / Lifecycle) instead of one flat run of eight tabs.
const TAB_GROUPS = [
  {
    label: 'Account',
    tabs: [
      { to: 'overview', label: 'Overview', icon: Building2 },
      { to: 'locations', label: 'Locations & deliveries', icon: MapPin },
      { to: 'users', label: 'Users', icon: Users },
    ],
  },
  {
    label: 'Billing',
    tabs: [
      { to: 'pricing', label: 'Pricing', icon: Tag },
      { to: 'billing', label: 'Billing & comms', icon: CreditCard },
      { to: 'paper', label: 'Paper / mail', icon: Printer },
    ],
  },
  {
    label: 'Lifecycle',
    tabs: [
      { to: 'requests', label: 'Change requests', icon: Inbox },
      { to: 'closure', label: 'Closure', icon: Archive },
    ],
  },
];

export default function AdminCustomerDetail() {
  const { customerId } = useParams<{ customerId: string }>();
  const { data: customer, isLoading, error, refetch } = useAdminCustomerDetail(customerId);
  // Pending-count badge for the Change requests tab.
  const { data: changeRequests } = useAdminAccountChangeRequests(customerId);
  const pendingCount = (changeRequests ?? []).filter((r) => r.status === 'pending').length;

  if (isLoading) {
    return (
      <div className="px-6 py-16 text-center text-sm text-brix-muted">
        <Loader2 size={16} className="animate-spin inline-block mr-2" />
        Loading customer…
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="px-6 py-10 space-y-4">
        <BackLink />
        <div className="rounded-xl border border-brix-warning/30 bg-brix-warning/5 px-4 py-3 text-sm text-brix-warning">
          {error instanceof Error
            ? error.message
            : 'This customer could not be loaded. It may not be enabled yet.'}
        </div>
      </div>
    );
  }

  const onHold = customer.on_credit_hold === true;

  return (
    <div className="px-6 py-6 space-y-5">
      <BackLink />

      {/* Header */}
      <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <CustomerNameHeading customerId={customer.id} name={customer.name} />
            <div className="mt-1.5 flex items-center gap-3 flex-wrap text-[11px] text-brix-muted">
              <span className="font-mono">QBO #{customer.qbo_customer_id}</span>
              <span className="inline-flex items-center gap-1">
                <Layers size={11} />
                {customer.location_mode === 'master'
                  ? `Master · ${customer.locations.length} location${customer.locations.length === 1 ? '' : 's'}`
                  : '1:1 account'}
              </span>
              {customer.payment_terms && <span>{customer.payment_terms}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <FavoriteToggle customerId={customer.id} />
            <SendInviteButton customerId={customer.id} customerName={customer.name} />
            <StartOrderButton customerId={customer.id} customerName={customer.name} />
            <ActAsButton customerId={customer.id} customerName={customer.name} />
            <AccountStatusBadge active={customer.active} onHold={onHold} />
          </div>
        </div>
      </div>

      {/* Tabs — grouped clusters (Account / Billing / Lifecycle) with labels. */}
      <nav className="flex items-end gap-4 border-b border-brix-border overflow-x-auto scrollbar-thin">
        {TAB_GROUPS.map((group, i) => (
          <div
            key={group.label}
            className={cn('shrink-0', i > 0 && 'border-l border-brix-border pl-4')}
          >
            <div className="px-1 mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-brix-muted/80 select-none">
              {group.label}
            </div>
            <div className="flex gap-1">
              {group.tabs.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    cn(
                      'group inline-flex items-center gap-2 px-3.5 py-2 text-sm rounded-t-lg cursor-pointer -mb-px border-b-2 whitespace-nowrap',
                      'transition-all duration-200',
                      isActive
                        ? 'border-brix-primary text-brix-primary font-semibold'
                        : 'border-transparent text-brix-muted hover:text-brix-ink',
                    )
                  }
                >
                  <Icon size={14} strokeWidth={1.8} />
                  {label}
                  {to === 'requests' && pendingCount > 0 && (
                    <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                      {pendingCount}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <Outlet context={{ customer, refetch } satisfies CustomerDetailContext} />
    </div>
  );
}

/**
 * Open a customer session in a NEW TAB, seeded via ?act_as (docs/ACTING-CONTEXT.md).
 * Per-tab context: the admin's current tab stays in /admin, and the new tab
 * carries its own acting-customer header — so two customers can be open at once
 * without stomping each other.
 */
function openAsCustomer(customerId: string, path: '/home' | '/order') {
  window.open(`${path}?act_as=${encodeURIComponent(customerId)}`, '_blank', 'noopener,noreferrer');
}

/** Phone-order shortcut: open the Shop as this customer in a new tab. */
function StartOrderButton({ customerId, customerName }: { customerId: string; customerName: string }) {
  return (
    <Button
      size="sm"
      onClick={() => openAsCustomer(customerId, '/order')}
      title={`Take a phone order for ${customerName} — opens their Shop in a new tab`}
    >
      <ShoppingCart size={13} />
      Start order
    </Button>
  );
}

/**
 * Browse the portal as this customer in a new tab (RLS scopes everything to
 * them via the per-tab acting header).
 */
function ActAsButton({ customerId, customerName }: { customerId: string; customerName: string }) {
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => openAsCustomer(customerId, '/home')}
      title={`Browse the portal as ${customerName} in a new tab — orders, invoices, cylinders, everything they see`}
    >
      <UserCog size={13} />
      Act as customer
    </Button>
  );
}

/**
 * Send this customer the "sign up on our new system" invite (links to
 * /welcome-back so they claim their account with their old login). Per-customer
 * on-demand send — the recipient is their Primary email on file.
 */
function SendInviteButton({ customerId, customerName }: { customerId: string; customerName: string }) {
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [msg, setMsg] = useState<string | null>(null);

  async function send() {
    setState('sending');
    setMsg(null);
    try {
      const res = await adminFetch<{ ok: true; sent: number; results: { error?: string }[] }>(
        'admin-send-cutover-invite',
        { method: 'POST', body: JSON.stringify({ customer_id: customerId, kind: 'cutover' }) },
      );
      if (res.sent > 0) {
        setState('sent');
      } else {
        setState('error');
        setMsg(res.results[0]?.error ?? 'not sent');
      }
    } catch (e) {
      setState('error');
      setMsg(e instanceof Error ? e.message : 'send failed');
    }
  }

  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => void send()}
      disabled={state === 'sending' || state === 'sent'}
      title={
        state === 'error'
          ? `Couldn't send: ${msg}`
          : `Email ${customerName} the sign-up invite (links to /welcome-back)`
      }
    >
      {state === 'sending' ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
      {state === 'sent' ? 'Invite sent' : state === 'error' ? 'Retry invite' : 'Send sign-up invite'}
    </Button>
  );
}

/**
 * Personal quick-list star: favorited customers show up in the sidebar
 * account switcher without listing the whole customer base.
 */
function FavoriteToggle({ customerId }: { customerId: string }) {
  const { data: memberships } = useMyMemberships();
  const setFavorite = useSetCustomerFavorite();
  const isFavorite = (memberships ?? []).some((m) => m.customerId === customerId && m.isFavorite);

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => setFavorite.mutate({ customerId, favorite: !isFavorite })}
      disabled={setFavorite.isPending}
      title={
        isFavorite
          ? 'Remove from your quick-switch favorites'
          : 'Add to your quick-switch favorites (sidebar account menu)'
      }
    >
      {setFavorite.isPending ? (
        <Loader2 size={13} className="animate-spin" />
      ) : (
        <Star size={13} className={isFavorite ? 'text-brix-accent fill-brix-accent' : ''} />
      )}
      {isFavorite ? 'Favorited' : 'Favorite'}
    </Button>
  );
}

/**
 * The account name, editable in place.
 *
 * The portal is the customer master and a name edit pushes to QuickBooks as
 * DisplayName — but until now the ONLY path that could change a name was an
 * approved company-name change request, which a CUSTOMER raises. Staff had no
 * way to fix one, which is how a franchise master sat as "M FOODS -" (trailing
 * space-dash, straight off the QBO display name) while every store created
 * under it inherited the dash into its qualified name.
 *
 * ⚠ QuickBooks requires DisplayName unique, so a rename can be refused; that
 * comes back as a note rather than a silent no-op, and the local rename
 * stands either way.
 */
function CustomerNameHeading({ customerId, name }: { customerId: string; name: string }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [note, setNote] = useState<string | null>(null);
  const rename = useSetCustomerName(customerId);

  if (!editing) {
    return (
      <>
        <div className="flex items-center gap-2.5 min-w-0">
          <Building2 size={18} className="text-brix-primary shrink-0" />
          <h1 className="font-display text-xl font-semibold text-brix-ink truncate">{name}</h1>
          <button
            type="button"
            aria-label="Rename this account"
            title="Rename — also updates the name in QuickBooks"
            onClick={() => {
              setNote(null);
              setValue(name);
              setEditing(true);
            }}
            className="shrink-0 text-brix-muted hover:text-brix-primary cursor-pointer"
          >
            <Pencil size={14} />
          </button>
        </div>
        {note && <p className="mt-1 text-[11px] text-amber-700">{note}</p>}
      </>
    );
  }

  const save = () => {
    setNote(null);
    rename.mutate(value, {
      onSuccess: (r) => {
        setEditing(false);
        setNote(r.push_notes.length > 0 ? r.push_notes.join(' · ') : null);
      },
      onError: (e) => setNote(e instanceof Error ? e.message : 'Rename failed'),
    });
  };

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <Building2 size={18} className="text-brix-primary shrink-0" />
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') setEditing(false);
          }}
          className="h-9 w-72 max-w-full rounded-lg border border-brix-border bg-brix-surface px-2.5 font-display text-lg font-semibold text-brix-ink"
        />
        <Button size="sm" onClick={save} disabled={rename.isPending || !value.trim()}>
          {rename.isPending ? <Loader2 size={12} className="animate-spin" /> : null}
          Save
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={rename.isPending}>
          Cancel
        </Button>
      </div>
      <p className="mt-1 text-[11px] text-brix-muted">
        Saved here first, then pushed to QuickBooks as the customer&rsquo;s display name. On a chain
        store this is the half after the colon — the parent supplies the rest.
      </p>
      {note && <p className="mt-1 text-[11px] text-amber-700">{note}</p>}
    </div>
  );
}

function BackLink() {
  return (
    <NavLink
      to="/admin/customers"
      className="inline-flex items-center gap-1.5 text-sm text-brix-muted hover:text-brix-ink cursor-pointer"
    >
      <ArrowLeft size={14} /> All customers
    </NavLink>
  );
}

function AccountStatusBadge({ active, onHold }: { active: boolean; onHold: boolean }) {
  if (onHold)
    return (
      <StatusBadge tone="danger" size="sm" icon={<Lock size={11} />}>
        Ordering locked
      </StatusBadge>
    );
  if (active)
    return (
      <StatusBadge tone="success" size="sm" icon={<ShieldCheck size={11} />}>
        Active
      </StatusBadge>
    );
  return (
    <StatusBadge tone="warning" size="sm" icon={<Mail size={11} />}>
      Deactivated
    </StatusBadge>
  );
}
